import { useMemo } from "react";
import { useStore } from "ra-core";

import type { ProspektFitConfig } from "@shared/fit.ts";
import type {
  DealStage,
  LabeledValue,
  LeadIntakeSettings,
  NoteStatus,
  ProjectStatus,
} from "../types";
import { defaultConfiguration } from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  companyColors: { color: string }[];
  companyName: string;
  currency: string;
  dealCategories: LabeledValue[];
  dealStages: DealStage[];
  noteStatuses: NoteStatus[];
  projectStatuses: ProjectStatus[];
  taskTypes: LabeledValue[];
  title: string;
  timeBillableByDefault: boolean;
  darkModeLogo: string;
  lightModeLogo: string;
  /** Lenke der kunder kan gi en anmeldelse ({instans.anmeldelseslenke}). */
  reviewUrl?: string;
  /** Instansens nettsted ({instans.nettsted}). */
  website?: string;
  /**
   * E-postmalene etter Vunnet (kap. 16.8) med flettefelt. Det som mangler,
   * faller tilbake til standardteksten (resolveWonEmailTemplates).
   */
  wonEmailTemplates?: {
    thanks?: { subject: string; body: string };
    review?: { subject: string; body: string };
  };
  /**
   * E-postinntaket for leads (pakke 5, kap. 6): lead-adresse, standard eier,
   * grenser og lister. Manglende felt gir standardverdiene
   * (resolveLeadIntakeSettings).
   */
  leadIntake?: LeadIntakeSettings;
  /** Årsverdi fra og med denne er «Høy verdi» på Pipeline. Tom = av. */
  dealHighValueThreshold?: number | null;
  /** Digitelle-fit for prospektering (P4). Se @shared/fit.ts. */
  prospektFit?: ProspektFitConfig;
  /**
   * Lagringstidsjobben (pipeline pakke 7, kap. 6.9 og AK44). Mangler = av.
   * Jobben sletter ingenting før administrator slår den på.
   */
  retention?: { enabled?: boolean };
  /**
   * Standardvisning på Oversikt per rolle (Oversikt «Fokus», fiks 7). Mangler
   * = administrator Team, medarbeider Mine. Brukerens eget valg går foran.
   */
  dashboardDefaults?: {
    admin?: "mine" | "team" | "unowned";
    member?: "mine" | "team" | "unowned";
  };
  /**
   * Felles skriveprofil for «Utkast med Claude» i e-postskriveren (neste
   * release, punkt 3). Skrives av administrator; tom = nøytral standard.
   */
  writingProfile?: string;
  googleWorkplaceDomain?: string;
  disableEmailPasswordAuthentication?: boolean;
}

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(() => {
    const merged = { ...defaultConfiguration, ...config };
    // Konfigurasjoner lagret før PR-5 mangler probability på salgsfasene:
    // fall tilbake til standardfasen med samme value, ellers 0, så vektet
    // pipelineverdi (graf/kolonnesummer) alltid har et tall å regne med.
    return {
      ...merged,
      dealStages: (merged.dealStages ?? []).map((stage) => ({
        ...stage,
        probability:
          stage.probability ??
          defaultConfiguration.dealStages.find((d) => d.value === stage.value)
            ?.probability ??
          0,
      })),
    };
  }, [config]);
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
